import type { ReactNode } from 'react';
import ReactReconciler from 'react-reconciler';

/**
 * Element type accepted by the JSX reconciler: a host element name (string)
 * or a function component.
 */
export type ElementType = string | ((props: Record<string, unknown>) => ReactNode);

/** Props bag passed through the reconciler. */
export type Props = Record<string, unknown>;

/**
 * Removes the `children` prop from a props object (children are handled by the
 * reconciler tree, never by node creators).
 */
export const omitChildren = (props: Props): Props => {
  const { children: _, ...rest } = props;
  return rest;
};

/**
 * Error callbacks forwarded to react-reconciler's `createContainer`. Defaults to
 * no-ops so renderers that do not care about render-time errors stay terse.
 */
export type ReconcilerErrorCallbacks = {
  onUncaughtError?: (error: unknown) => void;
  onCaughtError?: (error: unknown) => void;
  onRecoverableError?: (error: unknown) => void;
};

/**
 * The divergent half of a JSX-to-tree reconciler: how host nodes are created
 * and how children are wired into parents/containers. The shared factory owns
 * everything else (no-op host-config handlers, the transition-context stub, and
 * the react-reconciler v0.33.0 sync workaround).
 *
 * `NodeType` is the instance/child type, `TextNodeType` the text-instance type,
 * and `ContainerType` the root container type.
 */
export type JsxReconcilerStrategy<NodeType, TextNodeType extends NodeType, ContainerType> = {
  /** Create a host node from an element type and its (children-stripped) props. */
  createInstance: (type: ElementType, props: Props) => NodeType;
  /** Create a text node from a string. */
  createTextInstance: (text: string) => TextNodeType;

  /** Append a freshly created child during initial mount. */
  appendInitialChild: (parent: NodeType, child: NodeType) => void;
  /** Append a child to a parent during updates. */
  appendChild: (parent: NodeType, child: NodeType) => void;
  /** Append a child directly to the root container. */
  appendChildToContainer: (container: ContainerType, child: NodeType) => void;

  /** Insert a child before another child within a parent. */
  insertBefore: (parent: NodeType, child: NodeType, beforeChild: NodeType) => void;
  /** Insert a child before another child directly within the container. */
  insertInContainerBefore: (container: ContainerType, child: NodeType, beforeChild: NodeType) => void;

  /** Remove a child from a parent. */
  removeChild: (parent: NodeType, child: NodeType) => void;
  /** Remove a child directly from the container. */
  removeChildFromContainer: (container: ContainerType, child: NodeType) => void;

  /** Reset a container to an empty child set. */
  clearContainer: (container: ContainerType) => void;

  /** Apply a prop update to an already-committed instance (default: no-op). */
  commitUpdate?: (instance: NodeType, type: ElementType, prevProps: Props, nextProps: Props) => void;

  /** Update the text of a committed text instance. */
  commitTextUpdate: (textInstance: TextNodeType, oldText: string, newText: string) => void;
};

/**
 * A react-reconciler instance augmented with the v0.33.0 synchronous API.
 *
 * The shipped `@types/react-reconciler` advertises `flushSync`, but the 0.33.0
 * runtime exposes `updateContainerSync` / `flushSyncWork` instead. This is a
 * known types/runtime mismatch in the package, not something we can fix via
 * version alignment, so we narrow to the real runtime surface here.
 */
type ReconcilerWithSync<ContainerType> = ReturnType<
  typeof ReactReconciler<
    ElementType,
    Props,
    ContainerType,
    never,
    never,
    never,
    never,
    never,
    unknown,
    object,
    never,
    ReturnType<typeof setTimeout>,
    -1,
    null
  >
>;

/** Public handle returned by {@link createJsxReconciler}. */
export type JsxReconciler<_NodeType, ContainerType, OutputType> = {
  /**
   * Synchronously render `element` into a fresh container and project the
   * resulting tree to `OutputType`.
   *
   * @param element The React element to render.
   * @param createRoot Builds the empty root container/node for this render.
   * @param project Projects the populated root container to the final output.
   * @param errorCallbacks Optional error callbacks forwarded to `createContainer`.
   */
  renderSync: (
    element: ReactNode,
    createRoot: () => ContainerType,
    project: (root: ContainerType) => OutputType,
    errorCallbacks?: ReconcilerErrorCallbacks,
  ) => OutputType;
};

const noop = () => {
  /* noop */
};

/**
 * Builds a JSX-to-tree react-reconciler from a {@link JsxReconcilerStrategy}.
 *
 * The factory owns all of the boilerplate that is identical across renderers:
 * the `HostConfig` instantiation, the timing/context/priority/suspense no-op
 * handlers, the `HostTransitionContext` stub, and the v0.33.0
 * `updateContainerSync` / `flushSyncWork` synchronous-rendering workaround. The
 * strategy supplies only the node-creation and tree-wiring operations that
 * actually differ between renderers.
 */
export function createJsxReconciler<NodeType, TextNodeType extends NodeType, ContainerType, OutputType>(
  strategy: JsxReconcilerStrategy<NodeType, TextNodeType, ContainerType>,
): JsxReconciler<NodeType, ContainerType, OutputType> {
  /**
   * Host config for react-reconciler.
   * Implements the minimal interface needed for synchronous tree building.
   */
  const hostConfig: ReactReconciler.HostConfig<
    ElementType, // Type
    Props, // Props
    ContainerType, // Container
    NodeType, // Instance
    TextNodeType, // TextInstance
    NodeType, // SuspenseInstance
    NodeType, // HydratableInstance
    NodeType, // FormInstance
    NodeType, // PublicInstance
    object, // HostContext
    NodeType[], // ChildSet
    ReturnType<typeof setTimeout>, // TimeoutHandle
    -1, // NoTimeout
    null // TransitionStatus
  > = {
    supportsMutation: true,
    supportsPersistence: false,
    supportsHydration: false,
    isPrimaryRenderer: false, // Must be false for synchronous rendering without ReactDOM

    // Timing
    scheduleTimeout: setTimeout,
    cancelTimeout: clearTimeout,
    noTimeout: -1,
    supportsMicrotasks: true,
    scheduleMicrotask: globalThis.queueMicrotask ?? ((fn) => Promise.resolve().then(fn)),

    // Context
    getRootHostContext: () => ({}),
    getChildHostContext: (parentHostContext) => parentHostContext,
    getPublicInstance: (instance) => instance,

    // Instance creation
    createInstance: (type, props) => strategy.createInstance(type, omitChildren(props)),
    createTextInstance: (text) => strategy.createTextInstance(text),

    // Tree building
    appendInitialChild: (parentInstance, child) => strategy.appendInitialChild(parentInstance, child),
    appendChild: (parentInstance, child) => strategy.appendChild(parentInstance, child),
    appendChildToContainer: (container, child) => strategy.appendChildToContainer(container, child),

    // Insertion
    insertBefore: (parentInstance, child, beforeChild) => strategy.insertBefore(parentInstance, child, beforeChild),
    insertInContainerBefore: (container, child, beforeChild) =>
      strategy.insertInContainerBefore(container, child, beforeChild),

    // Removal
    removeChild: (parentInstance, child) => strategy.removeChild(parentInstance, child),
    removeChildFromContainer: (container, child) => strategy.removeChildFromContainer(container, child),

    // Finalization
    finalizeInitialChildren: () => false,
    prepareForCommit: () => null,
    resetAfterCommit: noop,
    preparePortalMount: noop,

    // Text content
    shouldSetTextContent: () => false,
    resetTextContent: noop,

    // Updates
    commitUpdate: (instance, type, prevProps, nextProps) =>
      strategy.commitUpdate?.(instance, type, prevProps, nextProps),
    commitTextUpdate: (textInstance, oldText, newText) => strategy.commitTextUpdate(textInstance, oldText, newText),

    // Container
    clearContainer: (container) => strategy.clearContainer(container),

    // Instance management
    getInstanceFromNode: () => null,
    beforeActiveInstanceBlur: noop,
    afterActiveInstanceBlur: noop,
    prepareScopeUpdate: noop,
    getInstanceFromScope: () => null,
    detachDeletedInstance: noop,

    // Priority
    setCurrentUpdatePriority: noop,
    getCurrentUpdatePriority: () => 16,
    resolveUpdatePriority: () => 16,

    // Form
    resetFormInstance: noop,

    // Callbacks
    requestPostPaintCallback: noop,

    // Transition
    shouldAttemptEagerTransition: () => false,
    trackSchedulerEvent: noop,
    resolveEventType: () => null,
    resolveEventTimeStamp: () => -1.1,

    // Suspense
    maySuspendCommit: () => false,
    preloadInstance: () => true,
    startSuspendingCommit: noop,
    suspendInstance: noop,
    waitForCommitToBeReady: () => null,

    // Transition context
    NotPendingTransition: null,
    // Required by react-reconciler interface but unused in synchronous rendering
    HostTransitionContext: {
      // biome-ignore lint/style/useNamingConvention: Required by react-reconciler
      $$typeof: Symbol.for('react.context'),
      Consumer: null as unknown as ReactReconciler.ReactContext<null>,
      Provider: null as unknown as ReactReconciler.ReactProviderType<null>,
      _currentValue: null,
      _currentValue2: null,
      _threadCount: 0,
    },
  };

  // Create reconciler instance.
  const reconcilerInstance = ReactReconciler(hostConfig) as ReconcilerWithSync<ContainerType> & {
    updateContainerSync: ReconcilerWithSync<ContainerType>['updateContainer'];
    flushSyncWork: () => void;
  };

  // Runtime check for sync API availability.
  if (
    typeof reconcilerInstance.updateContainerSync !== 'function' ||
    typeof reconcilerInstance.flushSyncWork !== 'function'
  ) {
    throw new Error(
      'react-reconciler sync API not available. ' +
        'Expected updateContainerSync and flushSyncWork methods. ' +
        `Found: updateContainerSync=${typeof reconcilerInstance.updateContainerSync}, ` +
        `flushSyncWork=${typeof reconcilerInstance.flushSyncWork}`,
    );
  }

  const renderSync = (
    element: ReactNode,
    createRoot: () => ContainerType,
    project: (root: ContainerType) => OutputType,
    errorCallbacks?: ReconcilerErrorCallbacks,
  ): OutputType => {
    const root = createRoot();
    const container = reconcilerInstance.createContainer(
      root,
      0, // LegacyRoot tag for sync rendering
      null, // hydrationCallbacks
      false, // isStrictMode
      null, // concurrentUpdatesByDefaultOverride
      '', // identifierPrefix
      errorCallbacks?.onUncaughtError ?? noop, // onUncaughtError
      errorCallbacks?.onCaughtError ?? noop, // onCaughtError
      errorCallbacks?.onRecoverableError ?? noop, // onRecoverableError
      noop, // onDefaultTransitionIndicator
    );

    // react-reconciler v0.33.0: updateContainerSync + flushSyncWork drive truly
    // synchronous rendering (see ReconcilerWithSync above).
    reconcilerInstance.updateContainerSync(element, container, null, noop);
    reconcilerInstance.flushSyncWork();

    return project(root);
  };

  return { renderSync };
}
